import { c as _c } from "react/compiler-runtime";
import type { Address, BigIntish, ChainId } from '@morpho-org/blue-sdk';
import { isDefined } from '@morpho-org/morpho-ts';
import { scrollToElementAfterDelay } from '@/helpers/scrollToElement';
import type { GuardianMetadataItems } from '../VaultHeroPendingActions/types';
import type { TimelockDecodedData } from './types';
import { VaultPendingTimelockChangeCap, VaultPendingTimelockChangeGuardian, VaultPendingTimelockChangeTimelock, VaultPendingTimelockCountdown, VaultPendingTimelockRemoveMarket } from './components';
import { StyledVaultPendingTimelock } from './VaultPendingTimelock.components';
const SCROLL_CONFIG = {
  risk: {
    elementId: 'risk-disclosures-section'
  },
  marketAllocation: {
    elementId: 'market-allocation-section'
  }
} as const;
interface VaultPendingTimelockProps {
  chainId: ChainId;
  marketSupplyCaps?: Record<string, BigIntish>;
  validAt: BigIntish;
  functionName: string;
  currentGuardian?: Address;
  currentGuardianMetadata?: GuardianMetadataItems;
  decodedData: TimelockDecodedData;
  currentTimelock?: BigIntish;
}
const VaultPendingTimelock = t0 => {
  const $ = _c(45);
  const {
    marketSupplyCaps,
    chainId,
    validAt,
    functionName,
    decodedData,
    currentGuardian,
    currentGuardianMetadata,
    currentTimelock
  } = t0;
  let t1;
  if ($[0] !== decodedData.__typename || $[1] !== decodedData.market || $[2] !== marketSupplyCaps) {
    t1 = decodedData.__typename === "VaultSetGuardianPendingData" || decodedData.__typename === "VaultSetTimelockPendingData" || decodedData.__typename === "VaultRemoveMarketPendingData" && isDefined(decodedData.market) || decodedData.__typename === "VaultSetCapPendingData" && isDefined(decodedData.market) && isDefined(marketSupplyCaps?.[decodedData.market.marketId]);
    $[0] = decodedData.__typename;
    $[1] = decodedData.market;
    $[2] = marketSupplyCaps;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isSelectable = t1;
  let t2;
  if ($[4] !== decodedData.__typename || $[5] !== isSelectable) {
    t2 = () => {
      if (!isSelectable) {
        return;
      }
      const config = decodedData.__typename === "VaultSetCapPendingData" || decodedData.__typename === "VaultRemoveMarketPendingData" ? SCROLL_CONFIG.marketAllocation : SCROLL_CONFIG.risk;
      scrollToElementAfterDelay(config.elementId);
    };
    $[4] = decodedData.__typename;
    $[5] = isSelectable;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleClick = t2;
  let t3;
  if ($[7] !== chainId || $[8] !== decodedData.__typename || $[9] !== decodedData.market || $[10] !== functionName) {
    t3 = decodedData.__typename === "VaultRemoveMarketPendingData" && isDefined(decodedData.market) && <VaultPendingTimelockRemoveMarket chainId={chainId} collateralAsset={decodedData.market.collateralAsset} loanAsset={decodedData.market.loanAsset} functionName={functionName} marketId={decodedData.market.marketId} />;
    $[7] = chainId;
    $[8] = decodedData.__typename;
    $[9] = decodedData.market;
    $[10] = functionName;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== chainId || $[13] !== decodedData.__typename || $[14] !== decodedData.market || $[15] !== decodedData.supplyCap || $[16] !== functionName || $[17] !== marketSupplyCaps) {
    t4 = decodedData.__typename === "VaultSetCapPendingData" && isDefined(decodedData.market) && <VaultPendingTimelockChangeCap chainId={chainId} collateralAsset={decodedData.market.collateralAsset} loanAsset={decodedData.market.loanAsset} supplyCap={decodedData.supplyCap} currentSupplyCap={marketSupplyCaps?.[decodedData.market.marketId]} functionName={functionName} marketId={decodedData.market.marketId} />;
    $[12] = chainId;
    $[13] = decodedData.__typename;
    $[14] = decodedData.market;
    $[15] = decodedData.supplyCap;
    $[16] = functionName;
    $[17] = marketSupplyCaps;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== chainId || $[20] !== currentGuardian || $[21] !== currentGuardianMetadata || $[22] !== decodedData.__typename || $[23] !== decodedData.guardian || $[24] !== functionName) {
    t5 = decodedData.__typename === "VaultSetGuardianPendingData" && <VaultPendingTimelockChangeGuardian chainId={chainId} functionName={functionName} currentGuardian={currentGuardian} currentGuardianMetadata={currentGuardianMetadata} newGuardian={decodedData.guardian.address} newGuardianMetadata={decodedData.guardian.metadata.items ?? undefined} />;
    $[19] = chainId;
    $[20] = currentGuardian;
    $[21] = currentGuardianMetadata;
    $[22] = decodedData.__typename;
    $[23] = decodedData.guardian;
    $[24] = functionName;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== currentTimelock || $[27] !== decodedData.__typename || $[28] !== decodedData.timelock || $[29] !== functionName) {
    t6 = decodedData.__typename === "VaultSetTimelockPendingData" && <VaultPendingTimelockChangeTimelock functionName={functionName} timelock={decodedData.timelock} currentTimelock={currentTimelock} />;
    $[26] = currentTimelock;
    $[27] = decodedData.__typename;
    $[28] = decodedData.timelock;
    $[29] = functionName;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  if ($[31] !== validAt) {
    t7 = <VaultPendingTimelockCountdown validAt={validAt} />;
    $[31] = validAt;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== handleClick || $[34] !== t3 || $[35] !== t4 || $[36] !== t5 || $[37] !== t6 || $[38] !== t7) {
    t8 = <div onClick={handleClick}>{t3}{t4}{t5}{t6}{t7}</div>;
    $[33] = handleClick;
    $[34] = t3;
    $[35] = t4;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
    $[39] = t8;
  } else {
    t8 = $[39];
  }
  let t9;
  if ($[40] !== decodedData.__typename || $[41] !== functionName || $[42] !== isSelectable || $[43] !== t8) {
    t9 = <StyledVaultPendingTimelock.Container selectable={isSelectable} data-function-name={functionName} data-typename={decodedData.__typename}>{t8}</StyledVaultPendingTimelock.Container>;
    $[40] = decodedData.__typename;
    $[41] = functionName;
    $[42] = isSelectable;
    $[43] = t8;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  return t9;
};
export default VaultPendingTimelock;